// /stacks/stocks/frontend/src/api.ts

export type Health = { ok: boolean; db_ok: boolean; time_utc: string };

export type MinuteBar = {
  symbol: string;
  ts: string; // ISO Z
  open: number; high: number; low: number; close: number;
  volume: number | null; trade_count: number | null; vwap: number | null;
};
export type DailyBar = MinuteBar;

export type TickerRow = {
  symbol: string; ts: string; price: number;
  open: number; high: number; low: number;
  volume: number | null; trade_count: number | null; vwap: number | null;
  prev_close: number | null; change: number | null; change_pct: number | null;
};

export type TwoDaysResp = {
  symbol: string;
  previous: { day: string; bars: MinuteBar[] };
  current: { day: string; bars: MinuteBar[] };
};

/* -------------------- API base + fallback logic -------------------- */

let _cachedBase: string | null = null;

function stripEnd(s: string) {
  return s.replace(/\/+$/, "");
}

/** Order of bases we’ll try, with VITE_API_BASE taking precedence if set. */
function baseCandidates(): string[] {
  const env = (import.meta as any).env?.VITE_API_BASE;
  if (env && typeof env === "string") {
    return [stripEnd(env)];
  }
  // If the app is hosted under /app/, prefer /app/api first.
  const path = (typeof window !== "undefined" && window.location?.pathname) || "/";
  const underApp = path.startsWith("/app/");
  return underApp ? ["/app/api", "/api"] : ["/api", "/app/api"];
}

function buildUrl(base: string, path: string) {
  const b = stripEnd(base);
  return `${b}${path.startsWith("/") ? path : `/${path}`}`;
}

/** Do one GET attempt against a specific base. */
async function attempt<T>(base: string, path: string, init: RequestInit, timeoutMs: number): Promise<T> {
  const ctrl = new AbortController();
  const to = setTimeout(() => ctrl.abort(), timeoutMs);
  try {
    const res = await fetch(buildUrl(base, path), {
      method: "GET",
      headers: { Accept: "application/json" },
      signal: ctrl.signal,
      ...init,
    });
    if (!res.ok) {
      const body = await res.text().catch(() => "");
      const msg = `HTTP ${res.status} ${res.statusText}${body ? ` – ${body.slice(0, 200)}` : ""}`;
      // Only let certain gateway-ish failures trigger a fallback.
      if (res.status === 502 || res.status === 504 || res.status === 523) {
        throw new Error(`__retry__ ${msg}`);
      }
      throw new Error(msg);
    }
    _cachedBase = base; // remember the working base
    return (await res.json()) as T;
  } finally {
    clearTimeout(to);
  }
}

/** Public GET that tries the primary base and then transparently falls back. */
async function get<T>(path: string, init?: RequestInit & { timeoutMs?: number }): Promise<T> {
  const { timeoutMs = 10_000, ...rest } = init || {};
  const tried: string[] = [];
  const candidates = _cachedBase ? [_cachedBase, ...baseCandidates().filter(b => b !== _cachedBase)] : baseCandidates();

  for (const base of candidates) {
    try {
      return await attempt<T>(base, path, rest, timeoutMs);
    } catch (e: any) {
      tried.push(base);
      const s = String(e || "");
      // Only continue to the next base on our special retry marker.
      if (!s.startsWith("__retry__")) {
        throw new Error(s.replace(/^__retry__\s*/, ""));
      }
      // else: loop to next base
    }
  }
  throw new Error(`All API bases failed (${tried.join(", ")}).`);
}

/** Small helper so the UI can show which base we’re using. */
export function getApiBaseInUse(): string {
  return _cachedBase || baseCandidates()[0];
}

/* -------------------- Endpoints -------------------- */

export const api = {
  health: () => get<Health>("/health", { cache: "no-store" }),
  symbols: () => get<string[]>("/symbols", { cache: "no-cache" }),
  minuteTwoDays: (symbol: string) =>
    get<TwoDaysResp>(`/minute/two-days?symbol=${encodeURIComponent(symbol)}`, { cache: "no-store" }),
  daily: (symbol: string, days = 30) =>
    get<DailyBar>(`/daily?symbol=${encodeURIComponent(symbol)}&days=${days}`, { cache: "no-cache" }) as unknown as Promise<DailyBar[]>,
  ticker: () => get<TickerRow[]>("/ticker", { cache: "no-store" }),
};
